import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getDriversReport } from "@/api/reports";
import { RatingStars } from "@/features/reports/components/RatingStars";
import { Skeleton } from "@/components/ui/Skeleton";
import type { ReportParams } from "@/types/reports";

interface Props {
  params: ReportParams;
}

/**
 * Compact recipient-ratings summary on the Reports page (TASK-3733): overall
 * average (weighted by each driver's ratings_count) + total ratings, plus a
 * per-driver mini distribution. Shares the cached drivers query so it never
 * issues a duplicate request. Never crashes on null/zero feedback — a tenant
 * with no ratings shows an empty-feedback message.
 */
export function RatingsSummaryCard({ params }: Props) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["reports", "drivers", params],
    queryFn: () => getDriversReport(params),
  });

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const rows = data?.results ?? [];
  const rated = rows.filter((r) => r.avg_rating != null && r.ratings_count > 0);
  const totalRatings = rated.reduce((s, r) => s + r.ratings_count, 0);
  const weightedSum = rated.reduce(
    (s, r) => s + (r.avg_rating as number) * r.ratings_count,
    0,
  );
  const overall = totalRatings > 0 ? weightedSum / totalRatings : null;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        {t("reports.ratingsSummary.title")}
      </p>

      {overall == null ? (
        <p className="py-4 text-center text-sm text-gray-400">
          {t("reports.ratingsSummary.noFeedback")}
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-gray-900">
              {overall.toFixed(1)}
            </span>
            <div className="flex flex-col">
              <RatingStars value={overall} size={16} />
              <span className="text-xs text-gray-400">
                {t("reports.ratingsSummary.ratingsCount", {
                  count: totalRatings,
                })}
              </span>
            </div>
          </div>

          {/* Per-driver mini distribution */}
          <div className="space-y-1.5">
            {rated.map((r) => (
              <div key={r.driver_id} className="flex items-center gap-2">
                <span className="w-28 truncate text-xs text-gray-500">
                  {r.driver_name}
                </span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-amber-400"
                    style={{
                      width: `${((r.avg_rating as number) / 5) * 100}%`,
                    }}
                  />
                </div>
                <span className="w-8 text-right text-xs font-medium text-gray-700">
                  {(r.avg_rating as number).toFixed(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
